import React, { useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCw, FiExternalLink, FiMonitor } = FiIcons;

const PreviewPanel = ({ files, activeFile }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    refreshPreview();
  }, [files, activeFile]);

  const refreshPreview = () => {
    if (!iframeRef.current || !activeFile || !files[activeFile]) return;

    const fileExtension = activeFile.split('.').pop()?.toLowerCase();
    
    // Handle different file types
    if (fileExtension === 'html') {
      renderHTMLFile();
    } else if (fileExtension === 'css') {
      renderCSSFile();
    } else if (['js', 'jsx', 'ts', 'tsx'].includes(fileExtension)) {
      renderJSFile();
    } else if (fileExtension === 'md') {
      renderMarkdownFile();
    } else if (fileExtension === 'json') {
      renderJSONFile();
    } else if (['svg'].includes(fileExtension)) {
      renderSVGFile();
    } else {
      renderTextFile();
    }
  };

  const renderHTMLFile = () => {
    const htmlContent = files[activeFile];
    const cssContent = files['style.css'] || files['styles.css'] || '';
    const jsContent = files['script.js'] || files['app.js'] || '';

    // Inject CSS and JS into HTML
    let modifiedHtml = htmlContent;

    // Add CSS
    if (cssContent) {
      const cssTag = `<style>${cssContent}</style>`;
      if (modifiedHtml.includes('</head>')) {
        modifiedHtml = modifiedHtml.replace('</head>', `${cssTag}</head>`);
      } else {
        modifiedHtml = `<head>${cssTag}</head>${modifiedHtml}`;
      }
    }

    // Add JS
    if (jsContent) {
      const jsTag = `<script>${jsContent}</script>`;
      if (modifiedHtml.includes('</body>')) {
        modifiedHtml = modifiedHtml.replace('</body>', `${jsTag}</body>`);
      } else {
        modifiedHtml = `${modifiedHtml}${jsTag}`;
      }
    }

    const blob = new Blob([modifiedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  const renderCSSFile = () => {
    const cssContent = files[activeFile];
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>CSS Preview</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0;
        padding: 20px;
        background: #f5f5f5;
      }
      .preview-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        margin-bottom: 20px;
      }
      .css-preview {
        white-space: pre-wrap;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 14px;
        line-height: 1.5;
        background: #f8f8f8;
        padding: 15px;
        border-radius: 4px;
        border-left: 4px solid #007acc;
        overflow-x: auto;
      }
      .demo-elements {
        margin-top: 20px;
        padding: 20px;
        border: 1px solid #ddd;
        border-radius: 4px;
      }
      ${cssContent}
    </style>
</head>
<body>
    <div class="preview-container">
        <h2>CSS Preview: ${activeFile}</h2>
        <div class="css-preview">${cssContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        
        <div class="demo-elements">
            <h3>Demo Elements</h3>
            <p>This paragraph can be styled with your CSS.</p>
            <button class="btn">Button Element</button>
            <div class="container">
                <span class="highlight">Highlighted text</span>
            </div>
            <ul>
                <li>List item 1</li>
                <li>List item 2</li>
                <li>List item 3</li>
            </ul>
        </div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  const renderJSFile = () => {
    const jsContent = files[activeFile];
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>JavaScript Preview</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0;
        padding: 20px;
        background: #f5f5f5;
      }
      .preview-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        margin-bottom: 20px;
      }
      .js-preview {
        white-space: pre-wrap;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 14px;
        line-height: 1.5;
        background: #f8f8f8;
        padding: 15px;
        border-radius: 4px;
        border-left: 4px solid #f7df1e;
        overflow-x: auto;
        max-height: 300px;
        overflow-y: auto;
      }
      .console-output {
        background: #1e1e1e;
        color: #d4d4d4;
        padding: 15px;
        border-radius: 4px;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 13px;
        margin-top: 10px;
        min-height: 100px;
      }
      .demo-area {
        margin-top: 20px;
        padding: 20px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: #fafafa;
      }
    </style>
</head>
<body>
    <div class="preview-container">
        <h2>JavaScript Preview: ${activeFile}</h2>
        <div class="js-preview">${jsContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        
        <div class="console-output" id="console">
            <div style="color: #569cd6;">Console Output:</div>
        </div>
        
        <div class="demo-area" id="demo-area">
            <h3>Demo Area</h3>
            <p>JavaScript execution results will appear here.</p>
            <button onclick="testFunction()">Test Function</button>
        </div>
    </div>

    <script>
        // Override console.log to show output in preview
        const originalLog = console.log;
        const consoleDiv = document.getElementById('console');
        
        console.log = function(...args) {
            originalLog.apply(console, arguments);
            const logDiv = document.createElement('div');
            logDiv.textContent = args.join(' ');
            logDiv.style.color = '#9cdcfe';
            consoleDiv.appendChild(logDiv);
        };
        
        console.error = function(...args) {
            const errorDiv = document.createElement('div');
            errorDiv.textContent = 'Error: ' + args.join(' ');
            errorDiv.style.color = '#f48771';
            consoleDiv.appendChild(errorDiv);
        };
        
        // Test function
        function testFunction() {
            console.log('Test function called!');
        }
        
        try {
            ${jsContent}
        } catch (error) {
            console.error(error.message);
        }
    </script>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  const renderMarkdownFile = () => {
    const mdContent = files[activeFile];
    // Simple markdown to HTML conversion
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Markdown Preview</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
        line-height: 1.6;
        color: #333;
      }
      h1, h2, h3, h4, h5, h6 {
        margin-top: 1.5em;
        margin-bottom: 0.5em;
      }
      h1 { font-size: 2em; border-bottom: 1px solid #eee; padding-bottom: 0.3em; }
      h2 { font-size: 1.5em; }
      h3 { font-size: 1.3em; }
      code {
        background: #f4f4f4;
        padding: 2px 4px;
        border-radius: 3px;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      }
      pre {
        background: #f8f8f8;
        padding: 15px;
        border-radius: 4px;
        overflow-x: auto;
        border-left: 4px solid #007acc;
      }
      blockquote {
        border-left: 4px solid #ddd;
        padding-left: 15px;
        color: #666;
        font-style: italic;
      }
      ul, ol {
        padding-left: 20px;
      }
      a {
        color: #007acc;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
    </style>
</head>
<body>
    <div id="content">${convertMarkdownToHTML(mdContent)}</div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  const renderJSONFile = () => {
    const jsonContent = files[activeFile];
    let formattedJSON = '';
    let isValidJSON = true;
    
    try {
      const parsed = JSON.parse(jsonContent);
      formattedJSON = JSON.stringify(parsed, null, 2);
    } catch (error) {
      isValidJSON = false;
      formattedJSON = jsonContent;
    }

    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>JSON Preview</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0;
        padding: 20px;
        background: #f5f5f5;
      }
      .preview-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      }
      .json-preview {
        white-space: pre-wrap;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 14px;
        line-height: 1.5;
        background: #f8f8f8;
        padding: 15px;
        border-radius: 4px;
        border-left: 4px solid ${isValidJSON ? '#28a745' : '#dc3545'};
        overflow-x: auto;
        max-height: 500px;
        overflow-y: auto;
      }
      .status {
        padding: 10px;
        border-radius: 4px;
        margin-bottom: 15px;
        font-weight: 500;
      }
      .valid {
        background: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
      }
      .invalid {
        background: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
      }
    </style>
</head>
<body>
    <div class="preview-container">
        <h2>JSON Preview: ${activeFile}</h2>
        <div class="status ${isValidJSON ? 'valid' : 'invalid'}">
            ${isValidJSON ? '✅ Valid JSON' : '❌ Invalid JSON - Check syntax'}
        </div>
        <div class="json-preview">${formattedJSON.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  const renderSVGFile = () => {
    const svgContent = files[activeFile];
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>SVG Preview</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0;
        padding: 20px;
        background: #f5f5f5;
      }
      .preview-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        text-align: center;
      }
      .svg-container {
        padding: 20px;
        background: #fafafa;
        border-radius: 4px;
        border: 1px solid #ddd;
        margin: 20px 0;
      }
      .code-preview {
        white-space: pre-wrap;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 12px;
        line-height: 1.4;
        background: #f8f8f8;
        padding: 15px;
        border-radius: 4px;
        border-left: 4px solid #ff6b6b;
        overflow-x: auto;
        text-align: left;
        max-height: 200px;
        overflow-y: auto;
      }
    </style>
</head>
<body>
    <div class="preview-container">
        <h2>SVG Preview: ${activeFile}</h2>
        <div class="svg-container">
            ${svgContent}
        </div>
        <h3>Source Code:</h3>
        <div class="code-preview">${svgContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  const renderTextFile = () => {
    const textContent = files[activeFile];
    const fileExtension = activeFile.split('.').pop()?.toLowerCase();
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Text Preview</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 0;
        padding: 20px;
        background: #f5f5f5;
      }
      .preview-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      }
      .text-preview {
        white-space: pre-wrap;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 14px;
        line-height: 1.5;
        background: #f8f8f8;
        padding: 15px;
        border-radius: 4px;
        border-left: 4px solid #6c757d;
        overflow-x: auto;
        max-height: 500px;
        overflow-y: auto;
      }
      .file-info {
        background: #e9ecef;
        padding: 10px;
        border-radius: 4px;
        margin-bottom: 15px;
        font-size: 14px;
        color: #495057;
      }
    </style>
</head>
<body>
    <div class="preview-container">
        <h2>Text Preview: ${activeFile}</h2>
        <div class="file-info">
            File Type: ${fileExtension?.toUpperCase() || 'Unknown'} • 
            Size: ${textContent.length} characters • 
            Lines: ${textContent.split('\n').length}
        </div>
        <div class="text-preview">${textContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    iframeRef.current.src = url;

    return () => URL.revokeObjectURL(url);
  };

  // Simple markdown to HTML converter
  const convertMarkdownToHTML = (markdown) => {
    return markdown
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      .replace(/^\* (.*$)/gm, '<li>$1</li>')
      .replace(/^- (.*$)/gm, '<li>$1</li>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>')
      .replace(/^/, '<p>')
      .replace(/$/, '</p>');
  };

  const openInNewTab = () => {
    if (!activeFile || !files[activeFile]) return;

    // Use the same rendering logic as the preview
    const fileExtension = activeFile.split('.').pop()?.toLowerCase();
    let content = '';

    if (fileExtension === 'html') {
      const htmlContent = files[activeFile];
      const cssContent = files['style.css'] || files['styles.css'] || '';
      const jsContent = files['script.js'] || files['app.js'] || '';

      content = htmlContent;
      if (cssContent) {
        const cssTag = `<style>${cssContent}</style>`;
        if (content.includes('</head>')) {
          content = content.replace('</head>', `${cssTag}</head>`);
        } else {
          content = `<head>${cssTag}</head>${content}`;
        }
      }
      if (jsContent) {
        const jsTag = `<script>${jsContent}</script>`;
        if (content.includes('</body>')) {
          content = content.replace('</body>', `${jsTag}</body>`);
        } else {
          content = `${content}${jsTag}`;
        }
      }
    } else {
      // For non-HTML files, create a simple display page
      content = `
<!DOCTYPE html>
<html>
<head>
    <title>${activeFile}</title>
    <style>
      body { font-family: monospace; padding: 20px; }
      pre { background: #f5f5f5; padding: 15px; border-radius: 4px; }
    </style>
</head>
<body>
    <h2>${activeFile}</h2>
    <pre>${files[activeFile].replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
</body>
</html>`;
    }

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 px-4 py-3 flex items-center justify-between border-b border-gray-800 flex-shrink-0">
        <h3 className="text-white font-semibold flex items-center text-sm">
          <SafeIcon icon={FiMonitor} className="mr-2 text-emerald-400" />
          Live Preview {activeFile && `- ${activeFile}`}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={refreshPreview}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
            title="Refresh Preview"
          >
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
          </button>
          <button
            onClick={openInNewTab}
            disabled={!activeFile || !files[activeFile]}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
            title="Open in New Tab"
          >
            <SafeIcon icon={FiExternalLink} className="text-sm" />
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-hidden">
        {activeFile && files[activeFile] ? (
          <iframe
            ref={iframeRef}
            className="w-full h-full border-0 bg-white"
            sandbox="allow-scripts allow-same-origin"
            title="Live Preview"
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-950">
            <div className="text-center text-gray-500 p-8">
              <SafeIcon icon={FiMonitor} className="text-6xl mb-4 mx-auto text-gray-600" />
              <p className="text-lg mb-2">No File Selected</p>
              <p className="text-sm">
                Select any file from the explorer to see its preview
              </p>
              <div className="mt-4 text-xs text-gray-400">
                <p>Supported: HTML, CSS, JS, JSON, Markdown, SVG, Text files</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewPanel;